import { GetterTree } from 'vuex'
import { RootState } from '@/store/types'
import Entry from '@/data/entry-model'
import { Maybe } from '@/types/general'
import { EntriesState, EntriesGetters } from './types'

const getters: GetterTree<EntriesState, RootState> = {
    /**
     * Get an entry by ID (curried)
     */
    getEntry({ allEntries }): (entry: string | Entry) => Entry {
        return (entry: string | Entry): Entry => {
            const entryId = typeof entry === 'string' ? entry : entry.id!
            return allEntries[entryId]
        }
    },

    /**
     * Get entry objects for a list of entry IDs
     */
    getEntries({ allEntries }): (entryIds: string[]) => Entry[] {
        return (entryIds: string[]) => entryIds.map(id => allEntries[id])
    },

    /**
     * Get vaults to show at the top level
     */
    topLevelEntries({ allEntries }): Entry[] {
        return Object.values(allEntries).filter(entry => entry.parentId === null)
    },

    /**
     * Get all vaults
     */
    allVaults({ allEntries }): Entry[] {
        return Object.values(allEntries).filter(entry => entry.isVault())
    },

    /**
     * Get the stack of Entries that are open in the tree view
     */
    openVaults({ treeStack }, { getEntry }: EntriesGetters): Entry[] {
        return treeStack.map(getEntry)
    },

    /**
     * The currently open vault in the tree
     */
    deepestVault(_s, { openVaults }: EntriesGetters): Maybe<Entry> {
        if (openVaults.length === 0) {
            return undefined
        }
        return openVaults[openVaults.length - 1]
    },

    /**
     * Get all entries that are descendants of the open vault
     *
     * Used to allow for searching in context
     */
    visibleEntries({ allEntries }, { deepestVault }: EntriesGetters): Entry[] {
        return deepestVault ? deepestVault.descendants : Object.values(allEntries)
    }
}

export default getters
